import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { faLock, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
  CustomStep as BaseCustomStep,
  DropdownComponent,
  TooltipOptions,
} from 'marketpro-design-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/services/auth.service';
import { AgeGroup } from '../../../models/age-group.model';
import { RangeFilter } from '../../../models/custom-filter-range.model';
import { RangeFilterType } from '../../../models/range-filter-type.enum';
import { DashboardStatisticsService } from '../../../services/dashboard-statistics.service';
import { DataFiltersService } from '../../../services/data-filters.service';
import { UpsaleModalService } from '../../../services/upsale-modal.service';

export interface CustomStep extends BaseCustomStep {
  customLabels?: { [key: number]: string };
}

@Component({
  selector: 'mpro-age-group-selector',
  templateUrl: './age-group-selector.component.html',
  styleUrls: [
    './../dropdown-common.scss',
    './age-group-selector.component.scss',
  ],
})
export class AgeGroupSelectorComponent implements OnInit, OnDestroy {
  @ViewChild(DropdownComponent) public dropdown: DropdownComponent;

  public faPlus = faPlus;
  public faLock = faLock;

  addNew: boolean = false;
  public inEditAgeGroup: RangeFilter = new RangeFilter('', 25, 50);
  public max: number = 76;
  public step: number = 1;
  public customSteps: CustomStep[] = [
    {
      from: 16,
      to: 18,
      step: 2,
      customLabels: {
        16: '16',
        18: '18',
      },
      hideStartingLabel: true,
    },
    {
      from: 21,
      to: 21,
      step: 1,
      customLabels: {
        21: '21',
      },
    },
    {
      from: 0,
      to: 75,
      step: 5,
      labelGap: 5,
      customLabels: {
        0: '0',
        5: '5',
        10: '10',
        15: '15',
        25: '25',
        30: '30',
        35: '35',
        40: '40',
        45: '45',
        50: '50',
        55: '55',
        60: '60',
        65: '65',
        70: '70',
        75: '75',
      },
      hideEndingLabel: true,
    },
    {
      from: 76,
      to: 76,
      step: 1,
      labelGap: 76,
      customLabels: {
        76: '>75',
      },
      considerToValueAsUndefined: true,
    },
  ];
  public isDeleteModalOpen: boolean = false;
  public ageGroupForDeletion: AgeGroup | undefined = undefined;
  @Input() public selectedAgeGroups: AgeGroup[] = [];

  @Input() public inputValue: string = '';
  @Input() public systemGroups: AgeGroup[] = [];
  @Input() public userGroups: AgeGroup[] = [];
  @Input() public hideNew: boolean = false;
  @Input() public multiSelect: boolean = false;
  @Input() public horizontal: boolean = false;

  @Output() public groupClicked: EventEmitter<AgeGroup> = new EventEmitter();
  @Output() public multiItemSelection: EventEmitter<AgeGroup[]> =
    new EventEmitter();
  @Output() public ageGroupsChanged: EventEmitter<void> = new EventEmitter();
  @ViewChild('tooltip')
  public tooltip: ElementRef;
  public tooltipOptions: TooltipOptions = {
    type: 'data-explorer',
    interactiveText:
      'You can’t select this because you already have have 5 age groups selected',
  };

  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(
    public authService: AuthService,
    private dashboardService: DashboardStatisticsService,
    private dataFiltersService: DataFiltersService,
    private upsaleModalService: UpsaleModalService
  ) {}

  public ngOnInit() {
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(({ ageGroup }) => {
        if (!ageGroup.preview) {
          this.setAddNew(false);
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public inputValues() {
    return this.selectedAgeGroups.map((r) => r.displayValue());
  }

  public onGroupClicked(group: AgeGroup) {
    if (group.disabled || group.authDisabled) {
      return;
    }
    if (this.multiSelect) {
      if (!group.greaterThan && !group.lessThan) {
        this.selectedAgeGroups = [];
        this.multiItemSelection.emit(this.selectedAgeGroups);
        this.dropdown.dropdownOpen = false;
        return;
      }
      if (
        this.selectedAgeGroups.some(
          (ageGroup) => ageGroup.displayValue() === group.displayValue()
        )
      ) {
        this.selectedAgeGroups = this.selectedAgeGroups.filter(
          (ageGroup) => ageGroup.displayValue() !== group.displayValue()
        );
      } else {
        this.selectedAgeGroups.push(group);
      }
      this.multiItemSelection.emit(this.selectedAgeGroups);
      this.dropdown.dropdownOpen = false;
      return;
    }
    this.groupClicked.emit(group);
    this.inputValue = group.displayValue();
    this.dropdown.dropdownOpen = false;
  }

  public isGroupSelected(group: AgeGroup) {
    if (this.multiSelect) {
      return this.inputValues().some(
        (inputValue) => group.displayValue() === inputValue
      );
    }
    if (group.id != undefined) {
      return (
        group.id === this.dataFiltersService.getDataFiltersValue().ageGroup.id
      );
    }

    return group.displayValue() === this.inputValue;
  }

  onSave(rangeFilter: RangeFilter) {
    rangeFilter.type = RangeFilterType.AGE;

    if (rangeFilter.id) {
      this.dashboardService
        .updateRangeCustomFilter(rangeFilter)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => this.closeCreateWindowAndReloadGroups());
      return;
    }

    this.dashboardService
      .saveRangeCustomFilter(rangeFilter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.closeCreateWindowAndReloadGroups());
  }

  private closeCreateWindowAndReloadGroups() {
    this.onCancel();
    this.ageGroupsChanged.emit();
  }

  setAddNew(state: boolean) {
    if (!this.authService.isLoggedIn() && state) {
      this.upsaleModalService.open();
      return;
    }
    this.addNew = state;
  }

  public onDropdownOpened() {
    this.ageGroupsChanged.emit();
  }

  public onDeleteClicked(ageGroup: AgeGroup) {
    if (ageGroup.id === undefined) {
      return;
    }

    this.ageGroupForDeletion = ageGroup;
    this.isDeleteModalOpen = true;
  }

  public onDelete() {
    if (!this.ageGroupForDeletion || !this.ageGroupForDeletion.id) {
      return;
    }

    this.isDeleteModalOpen = false;
    this.dashboardService
      .deleteRangeCustomFilter(this.ageGroupForDeletion.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.ageGroupForDeletion = undefined;
        this.ageGroupsChanged.emit();
      });
  }

  public onEditClicked(ageGroup: AgeGroup) {
    this.inEditAgeGroup = RangeFilter.fromAgeGroup(ageGroup);
    this.setAddNew(true);
  }

  public onPreview(rangeFilter: RangeFilter) {
    const ageGroup = AgeGroup.from(rangeFilter);
    ageGroup.preview = true;
    this.groupClicked.emit(ageGroup);
    this.dropdown.toggleDropdown();
  }

  public getInputValue() {
    if (!this.multiSelect || this.inputValues().length === 0) {
      return this.inputValue;
    }
    return this.inputValues().join(', ');
  }

  onCancel() {
    this.setAddNew(false);
    this.inEditAgeGroup = new RangeFilter('', 25, 50);
    if (this.dataFiltersService.getDataFiltersValue().ageGroup.preview) {
      this.groupClicked.emit(this.systemGroups[0]);
    }
  }
}
